/**
 * EditableInput - Generic editable single-line text field
 *
 * Works with useEditable hook - reads/writes from editable wrapper instead of maintaining internal state.
 * Supports both auto-save and custom save logic.
 *
 * @example
 * ```tsx
 * const editable = useEditable(product);
 *
 * // Auto-save on blur/enter
 * <EditableInput editable={editable} field="name" />
 *
 * // Custom save logic
 * <EditableInput
 *   editable={editable}
 *   field="name"
 *   autoSave={false}
 *   onSave={async (value) => {
 *     editable.name = value;
 *     editable.status = 'updated';
 *     await editable.save();
 *   }}
 * />
 * ```
 */

import React, { useState, useRef, useEffect } from 'react';
import { Input } from "@sourcepilot/ui/framework/ui/atoms/input";
import { Button } from "@sourcepilot/ui/framework/ui/atoms/button";
import { Edit2, Save, X } from 'lucide-react';
import type { Editable } from '@sourcepilot/ui/framework/data/hooks/use-editable';

// Hover highlight style for editable inputs
const HOVER_HIGHLIGHT_CLASS = 'border border-transparent hover:bg-gray-50 hover:border-gray-300 rounded px-2 py-1';

interface EditableInputProps<T> {
  editable: Editable<T> | null;
  field: keyof T;
  autoSave?: boolean;        // Default: true - auto-save on blur/enter
  onSave?: (value: string) => void | Promise<void>;  // Optional custom save logic
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  iconPosition?: 'right' | 'bottom';
  showEditIcon?: boolean;    // Default: true - show edit icon in display mode
  showSaveIcon?: boolean;    // Default: true - show save icon in edit mode
  showCancelIcon?: boolean;  // Default: true - show cancel icon in edit mode
  selectOnFocus?: boolean;   // Default: false - select all text on focus
  absoluteEditIcon?: boolean; // Default: false - position edit icon absolutely
  minWidth?: string;         // Default: '100px' - minimum width for the field
  hoverHighlight?: boolean;  // Default: false - adds hover border/background effect
  truncate?: boolean;        // Default: false - keep display text on one line with ellipsis when the container narrows
  overlayActions?: React.ReactNode; // Extra action buttons shown (with the edit icon) in a right-anchored hover overlay; only used in `truncate` mode
  children?: React.ReactNode; // Custom display content (replaces default text display)
}

export function EditableInput<T>({
  editable,
  field,
  autoSave = true,
  onSave,
  placeholder = '',
  className = '',
  disabled = false,
  iconPosition = 'right',
  showEditIcon = true,
  showSaveIcon = true,
  showCancelIcon = true,
  selectOnFocus = false,
  absoluteEditIcon = false,
  minWidth = '100px',
  hoverHighlight = false,
  truncate = false,
  overlayActions,
  children,
}: EditableInputProps<T>) {
  // Combine className with hover highlight if enabled
  const combinedClassName = hoverHighlight ? `${className} ${HOVER_HIGHLIGHT_CLASS}` : className;
  const [isEditing, setIsEditing] = useState(false);
  const [inputWidth, setInputWidth] = useState<number | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  // Get current value from editable wrapper
  const currentValue = editable ? String((editable as any)[field] || '') : '';

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (selectOnFocus) {
        inputRef.current.select();
      }
    }
  }, [isEditing, selectOnFocus]);

  const handleEdit = () => {
    // Measure the text width before entering edit mode
    if (measureRef.current) {
      const width = measureRef.current.offsetWidth;
      setInputWidth(width + 20); // Add padding for input chrome
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editable) return;

    try {
      if (onSave) {
        // Custom save logic
        await onSave(currentValue);
      } else if (autoSave) {
        // Auto-save already happened via onChange
        await editable.save();
      }
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save:', error);
      // Editable wrapper auto-reverts on error
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    // Discard optimistic changes
    if (editable) {
      editable.discard();
    }
    setIsEditing(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editable) return;
    // Write directly to editable wrapper (optimistic update)
    (editable as any)[field] = e.target.value;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (!editable || disabled) {
    return <div className={combinedClassName}>{currentValue || placeholder}</div>;
  }

  if (isEditing) {
    return (
      <div className={`inline-flex ${iconPosition === 'bottom' ? 'flex-col' : 'flex-row'} items-center gap-2`}>
        <Input
          ref={inputRef}
          value={currentValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}  // Auto-save on blur
          placeholder={placeholder}
          className="min-w-0"
          style={{ width: inputWidth ? `${inputWidth}px` : 'auto' }}
        />
        {(showSaveIcon || showCancelIcon) && (
          <div className="flex gap-1">
            {showSaveIcon && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleSave}
                className="h-8 w-8 p-0"
              >
                <Save className="h-4 w-4" style={{ color: '#00ccaa' }} />
              </Button>
            )}
            {showCancelIcon && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancel}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" style={{ color: '#666' }} />
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (absoluteEditIcon) {
    return (
      <div className={`group relative ${combinedClassName}`} style={{ textAlign: 'center', width: '100%' }}>
        <span ref={measureRef} style={{ display: 'inline-block' }}>
          {currentValue || <span className="text-gray-400">{placeholder}</span>}
        </span>
        {showEditIcon && (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleEdit();
            }}
            className="absolute top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
            style={{ right: '-12px', pointerEvents: 'auto' }}
          >
            <Edit2 className="h-3 w-3" style={{ color: '#00ccaa' }} />
          </Button>
        )}
      </div>
    );
  }

  // If children provided, use them for display mode (allows custom rendering like MiniAppText)
  if (children) {
    return (
      <div className={`group inline-flex items-center gap-1 ${hoverHighlight ? HOVER_HIGHLIGHT_CLASS : ''}`} style={{ minWidth }}>
        <span ref={measureRef} className="flex-1 min-w-0">
          {children}
        </span>
        {showEditIcon && (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleEdit();
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 flex-shrink-0"
          >
            <Edit2 className="h-3 w-3" style={{ color: '#00ccaa' }} />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={`group relative ${truncate ? 'flex w-full min-w-0' : 'inline-flex'} items-center gap-1 ${hoverHighlight ? HOVER_HIGHLIGHT_CLASS : ''}`} style={{ minWidth }}>
      <span
        ref={measureRef}
        onClick={!showEditIcon ? (e) => {
          e.stopPropagation();
          e.preventDefault();
          handleEdit();
        } : undefined}
        className={`${!showEditIcon ? 'cursor-pointer' : ''} ${truncate ? 'truncate min-w-0 flex-1' : ''} ${className}`}
        style={{ minWidth: truncate ? undefined : minWidth }}
      >
        {currentValue || <span className="text-gray-400 italic">{placeholder || 'Enter text...'}</span>}
      </span>
      {truncate ? (
        (showEditIcon || overlayActions) && (
          <div className="absolute right-0 inset-y-0 hidden group-hover:flex items-center gap-0.5 pl-8 bg-gradient-to-l from-background from-60% to-transparent">
            {showEditIcon && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleEdit();
                }}
                className="h-6 w-6 p-0 flex-shrink-0"
              >
                <Edit2 className="h-3 w-3" style={{ color: '#00ccaa' }} />
              </Button>
            )}
            {overlayActions}
          </div>
        )
      ) : (
        showEditIcon && (
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleEdit();
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 flex-shrink-0"
          >
            <Edit2 className="h-3 w-3" style={{ color: '#00ccaa' }} />
          </Button>
        )
      )}
    </div>
  );
}
